import React, { ReactNode, useState } from 'react';
import { Box, Card, CardContent, Grid, SxProps, Theme, Typography } from '@mui/material';
import { formatNumber } from '../../../helpers/utilityFunctions';
import ExpandIcon from '../../../components/ExpandIcon';

export interface ProgressItem {
  label?: string;
  value: number;
  expandableComponent?: ReactNode;
}

interface ProgressCardsProps {
  cards: ProgressItem[];
  outerStyle?: SxProps<Theme>;
  valueStyle?: SxProps<Theme>;
  boldValue?: boolean;
}

const ProgressCards: React.FC<ProgressCardsProps> = ({
  cards,
  outerStyle,
  valueStyle,
  boldValue,
}) => {
  const [expanded, setExpanded] = useState<number | null>(null);
  const expandedContent = expanded !== null ? cards[expanded]?.expandableComponent : null;

  return (
    <Grid className='progressCard' display='flex' flex={1} flexDirection='column' sx={outerStyle}>
      <Card
        sx={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: `repeat(${cards.length}, 1fr)`,
          columnGap: 2,
        }}
        variant='ghost'
        color='tertiary'>
        <CardContent
          sx={{
            display: 'contents',
            padding: '16px !important',
          }}>
          {cards.map((card, index) => {
            const expandable = typeof card.expandableComponent !== 'undefined';
            const isExpanded = expanded === index;

            const labeledCard = card.label;
            const rowOfLabeledCards = labeledCard && cards.length > 1;

            return (
              <Grid
                key={index}
                display='flex'
                flexDirection='column'
                whiteSpace='nowrap'
                justifyContent='space-between'
                maxWidth={rowOfLabeledCards ? '200px' : undefined}
                sx={{
                  'padding': '16px',
                  '@media (max-width: 1400px)': {
                    maxWidth: 'none',
                  },
                  '@media (max-width: 1900px)': {
                    whiteSpace: 'wrap',
                  },
                }}>
                {labeledCard && (
                  <Grid className='progressCardLabel' width='100%'>
                    <Typography
                      variant='p12SemiBold'
                      color='secondary.main'
                      lineHeight={1.5}
                      flexGrow={1}
                      display='flex'>
                      {card.label}
                    </Typography>
                  </Grid>
                )}
                <Grid
                  className='progressCardValueIcon'
                  display='flex'
                  alignItems={labeledCard ? 'flex-end' : 'center'}
                  justifyContent={rowOfLabeledCards ? '' : 'center'}
                  sx={valueStyle}>
                  <Grid
                    className='progressCardValue'
                    display='flex'
                    flexDirection='row'
                    alignItems='center'
                    marginBottom={expandable ? '-2px' : undefined}>
                    <Typography
                      variant='p20'
                      color='secondary.main'
                      fontWeight={boldValue ? 'bold' : ''}>
                      {formatNumber(card.value)}
                    </Typography>
                    {expandable && (
                      <ExpandIcon
                        isExpanded={isExpanded}
                        onToggle={() => setExpanded(isExpanded ? null : index)}
                        sx={{ padding: '0 0 0 8px' }}
                      />
                    )}
                  </Grid>
                </Grid>
              </Grid>
            );
          })}
        </CardContent>
      </Card>

      {expandedContent && (
        <Box
          mt={1}
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cards.length}, 1fr)`,
            columnGap: 2,
          }}>
          {expandedContent}
        </Box>
      )}
    </Grid>
  );
};

export default ProgressCards;
